// usePickSpeed — client-side rolling pick-rate counter.
//
// Each accepted item-scan calls `recordPick()`. The hook holds an
// in-memory array of recent timestamps and, on every render, returns
// the current items/hr rate using two formulae:
//
//   • 5+ minute session: count of picks within the trailing 5-minute
//     window x 12. This is the "rolling rate" — what the picker is
//     achieving right now, regardless of how long they've been on the
//     wave.
//   • <5 minute session: linearly extrapolated from how many picks
//     have happened since the very first pick. Without this a fresh
//     wave would read 0/hr for several minutes while the window fills.
//
// No persistence — resets on wave change. The audit-event timeline
// exists if we ever need server-side speed metrics.
import { useCallback, useEffect, useRef, useState } from "react";

const WINDOW_MS = 5 * 60 * 1000;   // 5 minutes
const TICK_MS   = 5_000;            // re-derive once every 5s so the
                                    // number ticks down naturally as
                                    // older picks age out of the window
                                    // even when the picker is idle.

export const usePickSpeed = () => {
    // Timestamps (ms epoch) of accepted item picks in this session,
    // newest at the end. Trimmed on each derive so the array doesn't
    // grow unbounded across a long wave.
    const timestampsRef = useRef<number[]>([]);
    const firstPickAtRef = useRef<number | null>(null);
    const [rate, setRate] = useState(0);

    const derive = useCallback(() => {
        const now = Date.now();
        const cutoff = now - WINDOW_MS;
        // Trim in place — splice mutates so the next render sees the
        // shorter array directly.
        let i = 0;
        while (i < timestampsRef.current.length && timestampsRef.current[i] < cutoff) i++;
        if (i > 0) timestampsRef.current.splice(0, i);

        const windowCount = timestampsRef.current.length;
        const firstAt = firstPickAtRef.current;
        if (firstAt == null || windowCount === 0) {
            setRate(0);
            return;
        }
        const sessionMs = now - firstAt;
        if (sessionMs < WINDOW_MS) {
            // <5 min — extrapolate from total picks in session, not the
            // (still-filling) window. items_per_min × 60.
            const totalSession = timestampsRef.current.length;
            const minutes = Math.max(0.5, sessionMs / 60_000);
            setRate(Math.round((totalSession / minutes) * 60));
        } else {
            // ≥5 min — rolling window. count × 12 (5-min slice × 12 = 1 hr).
            setRate(windowCount * 12);
        }
    }, []);

    const recordPick = useCallback(() => {
        const now = Date.now();
        if (firstPickAtRef.current == null) firstPickAtRef.current = now;
        timestampsRef.current.push(now);
        derive();
    }, [derive]);

    // Tick down when idle so a picker who stops for 5 minutes sees
    // their rate decay to 0 instead of being stuck at the last
    // pre-idle value.
    useEffect(() => {
        const id = window.setInterval(derive, TICK_MS);
        return () => window.clearInterval(id);
    }, [derive]);

    return { rate, recordPick };
};
